import { useCallback, useState, useEffect } from 'react';
import { AppBar, Toolbar, Box, IconButton, Typography, Collapse } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ASSET_PATHS } from '@core/constants/assets';
import { TEST_IDS } from '@core/constants/testIds';
import { HeaderUserMenu } from './HeaderUserMenu';
import { MENU_DATA } from '@core/data/menuData';
import { useAuthStore, useSettingsStore, useDirtyGuardStore, useUnsavedChangesDialogStore } from '@core/store';
import BaseSelector from '@/components/selectors/BaseSelector';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import { apiClient } from '@core/api/ApiClientFactory';
import { Role } from '@core/types';
import { useMobileHeaderPanel } from './MobileHeaderPanel';
import PanelTooltip from '@layouts/PanelTooltip.tsx';

interface HeaderProps {
    onMenuClick: () => void;
}

export const Header = ({ onMenuClick }: HeaderProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { isMobile, setHasMobilePanel, setIsShowFilterBar } = useSettingsStore();
    const { user, hasRole, updateOrganisation } = useAuthStore();
    const isSuperAdmin = hasRole(Role.SuperAdmin);
    const { panelContent, panelTitle, panelTooltip } = useMobileHeaderPanel();
    const hasMobilePanel = isSuperAdmin || panelContent != null;
    const hasAccessToMultipleOrganisations =
        isSuperAdmin || (hasRole(Role.OrganisationAdmin) && (user?.organisationClaims?.length ?? 0) > 1);

    const { isCurrentPageDirty, guard: dirtyGuard } = useDirtyGuardStore();
    const { openUnsavedChangesDialog } = useUnsavedChangesDialogStore();

    const organisationItems = (user?.organisationClaims ?? []).map((org) => ({
        text: org.name,
        value: org.id
    }));

    useEffect(() => {
        setHasMobilePanel(hasMobilePanel);
        setIsShowFilterBar(!!panelContent && !!panelTitle);
    }, [hasMobilePanel, panelContent, panelTitle]);

    const [mobileOrgPanelOpen, setMobileOrgPanelOpen] = useState(false);
    const selectedOrganisation = user?.organisation?.id ?? '';

    const getPageTitle = () => {
        const path = location.pathname;
        const findTitleInMenu = (menuItems: any[]): string | null => {
            for (const item of menuItems) {
                if (item.path === path) return item.pageName || item.title;
                if (item.items) {
                    const found = findTitleInMenu(item.items);
                    if (found) return found;
                }
            }
            return null;
        };
        for (const category of Object.values(MENU_DATA)) {
            const title = findTitleInMenu(category.items);
            if (title) return t(title);
        }
        return t('shop-control-center');
    };

    const getPageDescription = () => {
        const path = location.pathname;
        const findDescriptionInMenu = (menuItems: any[]): string | null => {
            for (const item of menuItems) {
                if (item.path === path) return item.description || null;
                if (item.items) {
                    const found = findDescriptionInMenu(item.items);
                    if (found) return found;
                }
            }
            return null;
        };
        for (const category of Object.values(MENU_DATA)) {
            const description = findDescriptionInMenu(category.items);
            if (description) return t(description);
        }
        return null;
    };

    const applyOrganisationChange = useCallback(
        (id: string) => {
            apiClient.setOrganisationId(id);
            updateOrganisation(id);
            eventBus.emit(EventTypes.ORGANISATION_CHANGED, id);
        },
        [updateOrganisation]
    );

    const handleChangeOrganisation = useCallback(
        (id: string) => {
            if (id === selectedOrganisation) return;

            if (isCurrentPageDirty()) {
                openUnsavedChangesDialog({
                    onDiscard: () => {
                        dirtyGuard?.discard?.();
                        applyOrganisationChange(id);
                    },
                    onStay: () => {},
                    onSaveAndLeave: dirtyGuard
                        ? async () => {
                              const saved = await dirtyGuard.save();
                              if (saved) applyOrganisationChange(id);
                              return saved;
                          }
                        : undefined
                });
                return;
            }

            applyOrganisationChange(id);
        },
        [selectedOrganisation, isCurrentPageDirty, dirtyGuard, applyOrganisationChange, openUnsavedChangesDialog]
    );

    return (
        <>
            <AppBar position="fixed" className="app-header Vuely-toolbar" elevation={0}>
                <Toolbar className="app-header-toolbar">
                    {isMobile ? (
                        <>
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="open drawer"
                                onClick={onMenuClick}
                                className="header-hamburger-menu"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Box className="header-page-title-wrapper">
                                <Typography variant="h6" component="h1" className="header-page-title">
                                    {getPageTitle()}
                                </Typography>
                            </Box>
                            {getPageDescription() && <PanelTooltip title={getPageDescription()!} />}
                        </>
                    ) : (
                        <>
                            <Box
                                className="header-logo-container"
                                onClick={() => navigate('/myshops')}
                                data-testid={TEST_IDS.HEADER.LOGO}
                            >
                                <img src={ASSET_PATHS.logos.wanzl} alt="Wanzl" className="header-logo" />
                            </Box>
                            <Box className="header-actions">
                                {hasAccessToMultipleOrganisations && (
                                    <Box className="header-desktop-org-wrapper">
                                        <Typography variant="body2" className="header-desktop-org-label">
                                            {t('selectOrganisation')}:
                                        </Typography>
                                        <BaseSelector
                                            value={selectedOrganisation}
                                            items={organisationItems}
                                            searchable
                                            onChange={(val) => handleChangeOrganisation(val)}
                                            className="header-desktop-org-selector"
                                        />
                                    </Box>
                                )}
                                <HeaderUserMenu />
                            </Box>
                        </>
                    )}
                </Toolbar>
            </AppBar>

            {isMobile && hasMobilePanel && (
                <Box className="header-mobile-panel">
                    {panelTitle && (
                        <Box className="header-mobile-panel-header-row">
                            <Typography variant="caption" className="header-mobile-panel-header-title">
                                {panelTitle}
                            </Typography>
                            {panelTooltip && <PanelTooltip title={panelTooltip} />}
                        </Box>
                    )}

                    <Box className="header-mobile-panel-scroll">
                        <Collapse in={mobileOrgPanelOpen}>
                            {hasAccessToMultipleOrganisations && (
                                <Box className="header-mobile-org-box">
                                    <BaseSelector
                                        label={t('organisation')}
                                        value={selectedOrganisation}
                                        items={organisationItems}
                                        searchable
                                        onChange={(val) => handleChangeOrganisation(val)}
                                        className="header-mobile-org-selector"
                                    />
                                </Box>
                            )}

                            {!!panelContent && <Box className="header-mobile-panel-content">{panelContent}</Box>}
                        </Collapse>
                    </Box>

                    <Box onClick={() => setMobileOrgPanelOpen((prev) => !prev)} className="header-mobile-panel-toggle">
                        <i
                            className={`bi ${mobileOrgPanelOpen ? 'bi-chevron-compact-up' : 'bi-chevron-compact-down'} header-mobile-panel-toggle-icon`}
                        />
                    </Box>
                </Box>
            )}
        </>
    );
};
